import { useCallback } from 'react'

import { TableDataCall } from 'components/Table/api'
import { TableGenericRequest, TableResponse } from 'components/Table/types'
import { FacilityListRequest } from 'features/facilities/types'
import {
  organizationCreate,
  getAllFeaturesList,
  deleteOrganization,
  getOrganizationById,
  updateOrganization,
  getFeatureList,
  getOrganizationDetails,
  questionBankDetailList,
  createOrUpdateQuestionBank,
  deleteQuestion,
  getCategories,
  addKeywords,
  getSavedKeywords,
  getWordBankDetailList,
  getCategoriesForAddMore,
  addCategory,
  updateCategory,
  deleteWordbank,
  getAllFacilities,
  organizationStatusUpdate,
  deleteCategory,
  updateQuestionBankPriorities,
  createHra,
  hasAIForQAConfigured,
  getRuleConfigById,
  createRule,
  updateRule,
  getRuleAssignRolesList,
  getRuleAssignUsersList,
  getCommunityCapability,
  deleteCommunityCapability,
  createCommunityCapability,
  updateCommunityCapability,
  uploadCommunityCapability,
  bulkUploadCentralizedFax,
  updateStatusCommunityCapability,
  getCommunityCapabilityById,
  getStatusThresholdConfigsByOrgId,
  getStatusThresholdByOrgAndStatus,
  saveStatusThresholdConfig,
  getOptOutStatusThresholdByOrgId,
  setOptOutStatusThreshold,
} from 'features/organization/api'
import {
  organizationDetails,
  organizationActions,
  selectOrganizationList,
  selectFeaturesList,
  selectQuestionBankDetail,
  selectQuestionList,
  selectedOrganization,
  getActiveTab,
  getEditQuestionItem,
  getActiveAppConfigTab,
  getSelectedOrgId,
  getSelectedRuleId,
  getOrgListFilterState,
  OrgListFilterState,
} from 'features/organization/store'
import {
  OrganizationDetailsFormInput,
  OrganizationDetailsRequest,
  PromiseFeatureForm,
  OrganizationCreateResponse,
  OrganizationList,
  OrganizationGetResponse,
  FeatureList,
  UserListRequest,
  FeatureListResponse,
  QuestionBankListDetail,
  QuestionBank,
  QuestionBankDetailResponse,
  AddKeywordDataResponse,
  AddKeywordRequest,
  WordBankSelectedDeatil,
  ListData,
  OrganizationStatusRequest,
  UpdateQuestionBankPriorityReq,
  AIForQAStatus,
  RulesListRequest,
  RuleAssignUserListReq,
  RulesAssignRoleListReq,
  CapabilityReq,
  CommunityCapabilityStatusReq,
  UploadCommunityCapabilityParams,
  UploadFileResponse,
  BulkUploadCentralizedFaxParams,
  BulkUploadCentralizedFaxResponse,
} from 'features/organization/types'
import { deleteRule } from 'features/referrals/api'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { DropDownEnvelope } from 'utils/types/DropDownEnvelope'
import { Envelope } from 'utils/types/Envelope'

import { getAllOrganizations } from '../../facilities/api'
import { CreateCategory, UpdateCategory } from '../types'
import { AddHraRequest } from '../types/hraTypes'
import { RulePayload } from '../types/rulesTypes'
import {
  GetOptOutStatusThresholdResponse,
  GetStatusThresholdByOrgAndStatusRequest,
  SaveStatusThresholdRequest,
  SaveStatusThresholdResponse,
  SetOptOutStatusThresholdRequest,
  SetOptOutStatusThresholdResponse,
  StatusThresholdDetailResponse,
  StatusThresholdListResponse,
} from '../types/statusThresholdTypes'

export type OrganizationServiceOperators = {
  organizationList: OrganizationList
  organizationInfo: OrganizationDetailsFormInput
  featuresList: FeatureList[]
  questionBankDetail: QuestionBankListDetail
  organizationCreation: (data: OrganizationDetailsRequest) => Promise<OrganizationCreateResponse>
  putOrganizationList: (data: object) => void
  putFeaturesList: (data: FeatureList[]) => void
  getAllFeatures: () => Promise<PromiseFeatureForm>

  deleteOrganization: (id: number) => Promise<Envelope>
  getOrganizationDetailsById: (id: number) => Promise<OrganizationGetResponse>
  putOrganizationData: (organizationData: OrganizationDetailsRequest) => Promise<Envelope>
  getOrganizationList: (data: TableGenericRequest, endPoint: string) => Promise<TableResponse>
  getDenialReasonGroup: (data: TableGenericRequest, endPoint: string) => Promise<TableResponse>
  getUserList: (data: UserListRequest, endPoint: string) => Promise<TableResponse>
  getFacilityList: (data: UserListRequest, endPoint: string) => Promise<TableResponse>
  getFeatureList: () => Promise<FeatureListResponse>
  getOrganizationDetail: (id: number) => Promise<OrganizationGetResponse>
  setQuestionBankListDetail: (data: QuestionBankListDetail) => void
  getQuestionBankList: (data: TableGenericRequest, endPoint: string) => Promise<TableResponse>
  getQuestionBankDetailList: (categoryId: number) => Promise<Envelope>
  addOrUpdateQuestionBankDetails: (data: QuestionBank) => Promise<Envelope>
  setQuestionList: (data: QuestionBankDetailResponse) => void
  questionList: QuestionBankDetailResponse
  deleteQuestion: (id: number) => Promise<Envelope>
  selectedOrganization: { id: number; name: string }
  putSelectedOrganization: (data: { id: number; name: string }) => void
  getActiveTab: number
  putActiveTab: (tab: number) => void
  organizationStatusChange: (orgStatus: OrganizationStatusRequest) => Promise<Envelope>
  updateQuestionBankPriority: (data: UpdateQuestionBankPriorityReq) => Promise<Envelope>
  hasAIEnabledForQA: (organizationId: number) => Promise<AIForQAStatus>
  setEditQuestionItem: (questionId: number) => void
  getEditQuestionItem?: number
  getActiveAppConfigTab: number
  selectedOrgId: number | null
  setSelectedOrgId: (id: number | null) => void
  selectedRuleId: number | null
  setSelectedRuleId: (id: number | null) => void
  getOrgListFilterState: OrgListFilterState
  putOrgListFilterState: (data: OrgListFilterState) => void
  putActiveAppConfigTab: (tab: number) => void
  getRulesList: (data: RulesListRequest, endPoint: string) => Promise<TableResponse>
  deleteRule: (id: number) => Promise<Envelope>
  getRuleConfigById: (id: number) => Promise<Envelope>
  createRule: (data: RulePayload) => Promise<Envelope>
  updateRule: (data: RulePayload) => Promise<Envelope>
  getRuleAssignRolesList: (data: RulesAssignRoleListReq) => Promise<Envelope>
  getRuleAssignUsersList: (data: RuleAssignUserListReq) => Promise<Envelope>
  getCommunityCapability: (data: RulesListRequest) => Promise<TableResponse>
  deleteCommunityCapability: (id: number) => Promise<Envelope>
  uploadCapabilities: (data: UploadCommunityCapabilityParams) => Promise<UploadFileResponse>
  bulkUploadCentralizedFax: (
    data: BulkUploadCentralizedFaxParams,
  ) => Promise<BulkUploadCentralizedFaxResponse>
  createCommunityCapability: (data: CapabilityReq) => Promise<Envelope>
  updateCommunityCapability: (data: CapabilityReq) => Promise<Envelope>
  updateCommunityCapabilityStatus: (data: CommunityCapabilityStatusReq) => Promise<Envelope>
  getCommunityCapabilityById: (id: number) => Promise<Envelope>
  getStatusThresholdConfigs: (organizationId: number) => Promise<StatusThresholdListResponse>
  getStatusThresholdByStatus: (
    payload: GetStatusThresholdByOrgAndStatusRequest,
  ) => Promise<StatusThresholdDetailResponse>
  saveStatusThreshold: (
    payload: SaveStatusThresholdRequest,
  ) => Promise<SaveStatusThresholdResponse>
  getOptOutStatusThreshold: (organizationId: number) => Promise<GetOptOutStatusThresholdResponse>
  saveOptOutStatusThreshold: (
    payload: SetOptOutStatusThresholdRequest,
  ) => Promise<SetOptOutStatusThresholdResponse>
}

export type CategoryServiceOperators = {
  getAllOrganization: () => Promise<Envelope>
  createCategory: (category: CreateCategory) => Promise<Envelope>
  updateCategory: (category: UpdateCategory) => Promise<Envelope>
  getCategoryList: (data: FacilityListRequest, endPoint: string) => Promise<TableResponse>
  deleteCategory: (id: number) => Promise<Envelope>
}
export type WordbankServiceOperators = {
  getAllOrganization: () => Promise<Envelope>
  getAllFacility: (id: number) => Promise<{ data: ListData[] }>
  getAllCategories: (id: number) => Promise<DropDownEnvelope>
  addKeyword: (data: AddKeywordRequest) => Promise<Envelope>
  getAllSavedKeywords: (data: AddKeywordDataResponse) => Promise<Envelope>
  getAllCategoriesForAddMore: (data: WordBankSelectedDeatil) => Promise<DropDownEnvelope>
  getWordBankList: (data: TableGenericRequest, endPoint: string) => Promise<TableResponse>
  getWordBankDetailList: (facilityId: number) => Promise<Envelope>
  updateSelectedDetail: (data: WordBankSelectedDeatil) => void
  // selectedDetail: WordBankSelectedDeatil
  putWordBankList: (data: object) => void
  // wordBankList: WordBankList
  deleteWordbank: (categoryId: number) => Promise<Envelope>
}

export const useOrganizationService = (): Readonly<OrganizationServiceOperators> => {
  const dispatch = useAppDispatch()
  return {
    selectedOrganization: useAppSelector(selectedOrganization),
    putSelectedOrganization: useCallback(
      data => dispatch(organizationActions.setSelectedOrganizationDetail(data)),
      [dispatch],
    ),
    organizationList: useAppSelector(selectOrganizationList),
    featuresList: useAppSelector(selectFeaturesList),
    questionBankDetail: useAppSelector(selectQuestionBankDetail),
    putFeaturesList: useCallback(
      data => {
        dispatch(organizationActions.setFeaturesList(data))
      },
      [dispatch],
    ),
    putOrganizationList: useCallback(
      data => {
        dispatch(organizationActions.setOrganizationList(data))
      },
      [dispatch],
    ),
    organizationInfo: useAppSelector(organizationDetails),
    organizationCreation: useCallback(
      (organizationData: OrganizationDetailsRequest) => organizationCreate(organizationData),
      [],
    ),
    getAllFeatures: useCallback(() => getAllFeaturesList(), []),

    deleteOrganization: useCallback((id: number) => deleteOrganization(id), []),
    getOrganizationDetailsById: useCallback((id: number) => getOrganizationById(id), []),
    putOrganizationData: useCallback(
      (organizationData: OrganizationDetailsRequest) => updateOrganization(organizationData),
      [],
    ),
    getOrganizationList: useCallback(
      (data: TableGenericRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    getDenialReasonGroup: useCallback(
      (data: TableGenericRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    getUserList: useCallback(
      (data: UserListRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    getFacilityList: useCallback(
      (data: UserListRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    getFeatureList: useCallback(() => getFeatureList(), []),
    getOrganizationDetail: useCallback((id: number) => getOrganizationDetails(id), []),
    setQuestionBankListDetail: useCallback(
      data => {
        dispatch(organizationActions.setQuestionBankListDetail(data))
      },
      [dispatch],
    ),
    getQuestionBankList: useCallback(
      (data: TableGenericRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    getQuestionBankDetailList: useCallback(
      (categoryId: number) => questionBankDetailList(categoryId),
      [],
    ),
    addOrUpdateQuestionBankDetails: useCallback(
      (data: QuestionBank) => createOrUpdateQuestionBank(data),
      [],
    ),
    setQuestionList: useCallback(
      data => {
        dispatch(organizationActions.setQuestionList(data))
      },
      [dispatch],
    ),
    questionList: useAppSelector(selectQuestionList),
    deleteQuestion: useCallback((id: number) => deleteQuestion(id), []),
    getActiveTab: useAppSelector(getActiveTab),
    putActiveTab: useCallback(
      tab => {
        dispatch(organizationActions.setActiveTab(tab))
      },
      [dispatch],
    ),
    organizationStatusChange: useCallback(
      (orgStatus: OrganizationStatusRequest) => organizationStatusUpdate(orgStatus),
      [],
    ),
    updateQuestionBankPriority: useCallback(
      (data: UpdateQuestionBankPriorityReq) => updateQuestionBankPriorities(data),
      [],
    ),
    hasAIEnabledForQA: useCallback((id: number) => hasAIForQAConfigured(id), []),
    setEditQuestionItem: useCallback(
      (questionId: number) => {
        dispatch(organizationActions.setEditQuestionItem(questionId))
      },
      [dispatch],
    ),
    getEditQuestionItem: useAppSelector(getEditQuestionItem),
    getActiveAppConfigTab: useAppSelector(getActiveAppConfigTab),
    putActiveAppConfigTab: useCallback(
      tab => {
        dispatch(organizationActions.setActiveAppConfigTab(tab))
      },
      [dispatch],
    ),
    getRulesList: useCallback(
      (data: RulesListRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    deleteRule: useCallback((id: number) => deleteRule(id), []),
    getRuleConfigById: useCallback((id: number) => getRuleConfigById(id), []),
    createRule: useCallback((data: RulePayload) => createRule(data), []),
    updateRule: useCallback((data: RulePayload) => updateRule(data), []),
    setSelectedOrgId: useCallback(
      (id: number | null) => {
        dispatch(organizationActions.setSelectedOrgId(id))
      },
      [dispatch],
    ),
    selectedOrgId: useAppSelector(getSelectedOrgId),
    setSelectedRuleId: useCallback(
      (id: number | null) => {
        dispatch(organizationActions.setSelectedRuleId(id))
      },
      [dispatch],
    ),
    selectedRuleId: useAppSelector(getSelectedRuleId),
    getOrgListFilterState: useAppSelector(getOrgListFilterState),
    putOrgListFilterState: useCallback(
      (data: OrgListFilterState) => {
        dispatch(organizationActions.setOrgListFilterState(data))
      },
      [dispatch],
    ),
    getRuleAssignRolesList: useCallback(
      (data: RulesAssignRoleListReq) => getRuleAssignRolesList(data),
      [],
    ),
    getRuleAssignUsersList: useCallback(
      (data: RuleAssignUserListReq) => getRuleAssignUsersList(data),
      [],
    ),
    getCommunityCapability: useCallback(
      (data: RulesListRequest) => getCommunityCapability(data),
      [],
    ),
    deleteCommunityCapability: useCallback((id: number) => deleteCommunityCapability(id), []),

    createCommunityCapability: useCallback(
      (data: CapabilityReq) => createCommunityCapability(data),
      [],
    ),
    updateCommunityCapability: useCallback(
      (data: CapabilityReq) => updateCommunityCapability(data),
      [],
    ),
    uploadCapabilities: useCallback(
      (data: UploadCommunityCapabilityParams) => uploadCommunityCapability(data),
      [],
    ),
    bulkUploadCentralizedFax: useCallback(
      (data: BulkUploadCentralizedFaxParams) => bulkUploadCentralizedFax(data),
      [],
    ),

    updateCommunityCapabilityStatus: useCallback(
      (data: CommunityCapabilityStatusReq) => updateStatusCommunityCapability(data),
      [],
    ),
    getCommunityCapabilityById: useCallback((id: number) => getCommunityCapabilityById(id), []),
    getStatusThresholdConfigs: useCallback(
      (organizationId: number) => getStatusThresholdConfigsByOrgId(organizationId),
      [],
    ),
    getStatusThresholdByStatus: useCallback(
      (payload: GetStatusThresholdByOrgAndStatusRequest) =>
        getStatusThresholdByOrgAndStatus(payload),
      [],
    ),
    saveStatusThreshold: useCallback(
      (payload: SaveStatusThresholdRequest) => saveStatusThresholdConfig(payload),
      [],
    ),
    getOptOutStatusThreshold: useCallback(
      (organizationId: number) => getOptOutStatusThresholdByOrgId(organizationId),
      [],
    ),
    saveOptOutStatusThreshold: useCallback(
      (payload: SetOptOutStatusThresholdRequest) => setOptOutStatusThreshold(payload),
      [],
    ),
  }
}
export const useCategoryService = (): Readonly<CategoryServiceOperators> => ({
  getAllOrganization: useCallback(() => getAllOrganizations(), []),
  createCategory: useCallback((category: CreateCategory) => addCategory(category), []),
  updateCategory: useCallback((category: UpdateCategory) => updateCategory(category), []),

  getCategoryList: useCallback(
    (data: FacilityListRequest, endPoint: string) => TableDataCall(data, endPoint),
    [],
  ),
  deleteCategory: useCallback((id: number) => deleteCategory(id), []),
})
export const useWordbankService = (): Readonly<WordbankServiceOperators> => {
  const dispatch = useAppDispatch()
  return {
    getAllOrganization: useCallback(() => getAllOrganizations(), []),
    getAllFacility: useCallback((id: number) => getAllFacilities(id), []),
    getAllCategories: useCallback((id: number) => getCategories(id), []),
    addKeyword: useCallback((data: AddKeywordRequest) => addKeywords(data), []),
    getAllSavedKeywords: useCallback((data: AddKeywordDataResponse) => getSavedKeywords(data), []),
    getAllCategoriesForAddMore: useCallback(
      (data: WordBankSelectedDeatil) => getCategoriesForAddMore(data),
      [],
    ),
    getWordBankList: useCallback(
      (data: TableGenericRequest, endPoint: string) => TableDataCall(data, endPoint),
      [],
    ),
    getWordBankDetailList: useCallback(
      (facilityId: number) => getWordBankDetailList(facilityId),
      [],
    ),
    updateSelectedDetail: useCallback(
      data => {
        dispatch(organizationActions.setSelectedDetail(data))
      },
      [dispatch],
    ),

    putWordBankList: useCallback(
      data => {
        dispatch(organizationActions.setWordBankList(data))
      },
      [dispatch],
    ),

    deleteWordbank: useCallback((categoryId: number) => deleteWordbank(categoryId), []),
  }
}

type HraServiceOperators = {
  createHra: (data: AddHraRequest) => Promise<Envelope>
  getHraList: (data: FacilityListRequest, endPoint: string) => Promise<TableResponse>
}

export const useHraService = (): Readonly<HraServiceOperators> => ({
  createHra: useCallback((data: AddHraRequest) => createHra(data), []),
  getHraList: useCallback(
    (data: FacilityListRequest, endPoint: string) => TableDataCall(data, endPoint),
    [],
  ),
})

export default useOrganizationService
